import { createRoutine } from 'redux-routines';
import { INIT, LOADING, SUCCESS, FAILURE } from '@constants/requestPhase';
import * as api from './api';

// ─── Routines ──────────────────────────────────────────────────────────────

const getRolesRoutine = createRoutine('SCHEDULING/GET_ROLES');
const createRoleRoutine = createRoutine('SCHEDULING/CREATE_ROLE');
const updateRoleRoutine = createRoutine('SCHEDULING/UPDATE_ROLE');
const deleteRoleRoutine = createRoutine('SCHEDULING/DELETE_ROLE');

const getEmployeesRoutine = createRoutine('SCHEDULING/GET_EMPLOYEES');
const createEmployeeRoutine = createRoutine('SCHEDULING/CREATE_EMPLOYEE');
const updateEmployeeRoutine = createRoutine('SCHEDULING/UPDATE_EMPLOYEE');
const deleteEmployeeRoutine = createRoutine('SCHEDULING/DELETE_EMPLOYEE');

const getWeekScheduleRoutine = createRoutine('SCHEDULING/GET_WEEK_SCHEDULE');
const createShiftRoutine = createRoutine('SCHEDULING/CREATE_SHIFT');
const updateShiftRoutine = createRoutine('SCHEDULING/UPDATE_SHIFT');
const deleteShiftRoutine = createRoutine('SCHEDULING/DELETE_SHIFT');
const publishWeekRoutine = createRoutine('SCHEDULING/PUBLISH_WEEK');

const getTimeOffRoutine = createRoutine('SCHEDULING/GET_TIME_OFF');
const createTimeOffRoutine = createRoutine('SCHEDULING/CREATE_TIME_OFF');
const reviewTimeOffRoutine = createRoutine('SCHEDULING/REVIEW_TIME_OFF');

const getShiftTradesRoutine = createRoutine('SCHEDULING/GET_TRADES');
const reviewShiftTradeRoutine = createRoutine('SCHEDULING/REVIEW_TRADE');

// ─── Thunks ────────────────────────────────────────────────────────────────

const createThunk = (routine, request) => data => async dispatch => {
  try {
    dispatch(routine.request());
    const response = await request(data);
    return dispatch(routine.success(response.data));
  } catch (error) {
    return dispatch(routine.failure(error?.response?.data));
  } finally {
    dispatch(routine.fulfill());
  }
};

export const getRoles = createThunk(getRolesRoutine, api.default.getRoles);
export const createRole = createThunk(createRoleRoutine, api.default.createRole);
export const updateRole = createThunk(updateRoleRoutine, api.default.updateRole);
export const deleteRole = createThunk(deleteRoleRoutine, api.default.deleteRole);

export const getEmployees = createThunk(getEmployeesRoutine, api.default.getEmployees);
export const createEmployee = createThunk(createEmployeeRoutine, api.default.createEmployee);
export const updateEmployee = createThunk(updateEmployeeRoutine, api.default.updateEmployee);
export const deleteEmployee = createThunk(deleteEmployeeRoutine, api.default.deleteEmployee);

export const getWeekSchedule = createThunk(getWeekScheduleRoutine, api.default.getWeekSchedule);
export const createShift = createThunk(createShiftRoutine, api.default.createShift);
export const updateShift = createThunk(updateShiftRoutine, api.default.updateShift);
export const deleteShift = createThunk(deleteShiftRoutine, api.default.deleteShift);
export const publishWeek = createThunk(publishWeekRoutine, api.default.publishWeek);

export const getTimeOffRequests = createThunk(getTimeOffRoutine, api.default.getTimeOffRequests);
export const createTimeOffRequest = createThunk(createTimeOffRoutine, api.default.createTimeOffRequest);
export const reviewTimeOffRequest = createThunk(reviewTimeOffRoutine, api.default.reviewTimeOffRequest);

export const getShiftTrades = createThunk(getShiftTradesRoutine, api.default.getShiftTrades);
export const reviewShiftTrade = createThunk(reviewShiftTradeRoutine, api.default.reviewShiftTrade);

// ─── Initial State ─────────────────────────────────────────────────────────

const initialState = {
  roles: [],
  employees: [],
  shifts: [],
  reservationCounts: {}, // { 'YYYY-MM-DD': coverCount }
  timeOffRequests: [],
  shiftTrades: [],

  getRolesPhase: INIT,
  getEmployeesPhase: INIT,
  getWeekSchedulePhase: INIT,
  createShiftPhase: INIT,
  updateShiftPhase: INIT,
  deleteShiftPhase: INIT,
  publishWeekPhase: INIT,
  getTimeOffPhase: INIT,
  reviewTimeOffPhase: INIT,
  getShiftTradesPhase: INIT,
  reviewShiftTradePhase: INIT,

  error: null,
};

// ─── Reducer ───────────────────────────────────────────────────────────────

const schedulingReducer = (state = initialState, action) => {
  switch (action.type) {
    // Roles
    case getRolesRoutine.REQUEST:
      return { ...state, getRolesPhase: LOADING };
    case getRolesRoutine.SUCCESS:
      return { ...state, getRolesPhase: SUCCESS, roles: action.payload };
    case getRolesRoutine.FAILURE:
      return { ...state, getRolesPhase: FAILURE };

    case createRoleRoutine.SUCCESS:
      return { ...state, roles: [...state.roles, action.payload] };
    case updateRoleRoutine.SUCCESS:
      return {
        ...state,
        roles: state.roles.map(r => (r.id === action.payload.id ? action.payload : r)),
      };
    case deleteRoleRoutine.SUCCESS:
      return {
        ...state,
        roles: state.roles.filter(r => r.id !== action.payload.id),
      };

    // Employees
    case getEmployeesRoutine.REQUEST:
      return { ...state, getEmployeesPhase: LOADING };
    case getEmployeesRoutine.SUCCESS:
      return { ...state, getEmployeesPhase: SUCCESS, employees: action.payload };
    case getEmployeesRoutine.FAILURE:
      return { ...state, getEmployeesPhase: FAILURE };

    case createEmployeeRoutine.SUCCESS:
      return { ...state, employees: [...state.employees, action.payload] };
    case updateEmployeeRoutine.SUCCESS:
      return {
        ...state,
        employees: state.employees.map(e => (e.id === action.payload.id ? action.payload : e)),
      };
    case deleteEmployeeRoutine.SUCCESS:
      return {
        ...state,
        employees: state.employees.filter(e => e.id !== action.payload.id),
      };

    // Week schedule
    case getWeekScheduleRoutine.REQUEST:
      return { ...state, getWeekSchedulePhase: LOADING };
    case getWeekScheduleRoutine.SUCCESS:
      return {
        ...state,
        getWeekSchedulePhase: SUCCESS,
        shifts: action.payload.shifts,
        reservationCounts: action.payload.reservationCounts,
      };
    case getWeekScheduleRoutine.FAILURE:
      return { ...state, getWeekSchedulePhase: FAILURE };

    case createShiftRoutine.REQUEST:
      return { ...state, createShiftPhase: LOADING };
    case createShiftRoutine.SUCCESS:
      return { ...state, createShiftPhase: SUCCESS, shifts: [...state.shifts, action.payload] };
    case createShiftRoutine.FAILURE:
      return { ...state, createShiftPhase: FAILURE, error: action.payload };
    case createShiftRoutine.FULFILL:
      return { ...state, createShiftPhase: INIT };

    case updateShiftRoutine.REQUEST:
      return { ...state, updateShiftPhase: LOADING };
    case updateShiftRoutine.SUCCESS:
      return {
        ...state,
        updateShiftPhase: SUCCESS,
        shifts: state.shifts.map(s => (s.id === action.payload.id ? action.payload : s)),
      };
    case updateShiftRoutine.FULFILL:
      return { ...state, updateShiftPhase: INIT };

    case deleteShiftRoutine.SUCCESS:
      return {
        ...state,
        shifts: state.shifts.filter(s => s.id !== action.payload.id),
      };

    case publishWeekRoutine.REQUEST:
      return { ...state, publishWeekPhase: LOADING };
    case publishWeekRoutine.SUCCESS:
      return {
        ...state,
        publishWeekPhase: SUCCESS,
        shifts: state.shifts.map(s =>
          s.status === 'draft' ? { ...s, status: 'published' } : s,
        ),
      };
    case publishWeekRoutine.FAILURE:
      return { ...state, publishWeekPhase: FAILURE };
    case publishWeekRoutine.FULFILL:
      return { ...state, publishWeekPhase: INIT };

    // Time-off
    case getTimeOffRoutine.REQUEST:
      return { ...state, getTimeOffPhase: LOADING };
    case getTimeOffRoutine.SUCCESS:
      return { ...state, getTimeOffPhase: SUCCESS, timeOffRequests: action.payload };
    case getTimeOffRoutine.FAILURE:
      return { ...state, getTimeOffPhase: FAILURE };

    case reviewTimeOffRoutine.SUCCESS:
      return {
        ...state,
        timeOffRequests: state.timeOffRequests.map(r =>
          r.id === action.payload.id ? action.payload : r,
        ),
      };

    // Trades
    case getShiftTradesRoutine.REQUEST:
      return { ...state, getShiftTradesPhase: LOADING };
    case getShiftTradesRoutine.SUCCESS:
      return { ...state, getShiftTradesPhase: SUCCESS, shiftTrades: action.payload };
    case getShiftTradesRoutine.FAILURE:
      return { ...state, getShiftTradesPhase: FAILURE };

    case reviewShiftTradeRoutine.SUCCESS:
      return {
        ...state,
        shiftTrades: state.shiftTrades.map(t =>
          t.id === action.payload.id ? action.payload : t,
        ),
      };

    default:
      return state;
  }
};

export const schedulingStorePersistWhitelist = ['roles', 'employees'];

export default schedulingReducer;
